import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import api from "../lib/api";

const WorkspaceBrandingContext = createContext(null);

export const WorkspaceBrandingProvider = ({ children }) => {
    const { user } = useAuth();
    const tenantId = user?.tenant_id;
    const [savedName, setSavedName] = useState(null);
    const revision = useRef(0);

    const updateBranding = useCallback((profile) => {
        if (!tenantId || profile?.tenant_id !== tenantId) return;
        // A late initial fetch must not overwrite a newer successful save.
        revision.current += 1;
        setSavedName({ tenantId, name: (profile.business_name || "").trim() });
    }, [tenantId]);

    useEffect(() => {
        if (!tenantId) return undefined;
        let cancelled = false;
        const version = ++revision.current;
        api.get("/business-profile").then(({ data }) => {
            if (!cancelled && version === revision.current && data?.tenant_id === tenantId) {
                setSavedName({ tenantId, name: (data.business_name || "").trim() });
            }
        }).catch(() => {
            // Keep the workspace fallback; profile errors are handled on its page.
        });
        return () => { cancelled = true; };
    }, [tenantId]);

    const workspaceName = user?.tenant?.business_name || "Workspace";
    const fallbackName = user?.role === "admin" && workspaceName === "Boundary HQ"
        ? "WebJesters HQ" : workspaceName;
    const profileName = savedName?.tenantId === tenantId ? savedName.name : "";
    const legacyAdminProfile = user?.role === "admin" && profileName === "Boundary HQ";
    const businessName = profileName && !legacyAdminProfile ? profileName : fallbackName;

    return (
        <WorkspaceBrandingContext.Provider value={{ businessName, updateBranding }}>
            {children}
        </WorkspaceBrandingContext.Provider>
    );
};

export const useWorkspaceBranding = () => {
    const value = useContext(WorkspaceBrandingContext);
    if (!value) throw new Error("useWorkspaceBranding requires WorkspaceBrandingProvider");
    return value;
};